import { ErrorManagement } from '@printdeal/error-management/react';
import Api from '../Api';

export interface GetApiCredentialsResult {
  isActive: boolean;
  apiKey: string;
  storeKey: string;
  createdAt: string;
  displaySecret: string;
  description: string;
  createdBy: string;
  lastModifiedAt: string;
  lastUsedAt: string;
  customerId: string;
}

export interface CreateApiCredentialsResult {
  apiKey: string;
  secret: string;
}

export interface UpdateCredentialsRequestPayload {
  description?: string;
  isActive?: boolean;
  apiKey: string;
}

class ApiCredentialsService extends Api {
  async getApiCredentials(
    authToken: string,
  ): Promise<GetApiCredentialsResult[]> {
    try {
      const { data } = await this.httpClient.get('/credentials', { headers: { Authorization: authToken } });
      return data;
    } catch (error) {
      ErrorManagement.exception({
        exception: error,
        tags: { fileLocation: 'ApiCredentialsService', featureFunction: 'getApiCredentials' },
        fingerprint: ['api-credentials-service-get'],
      });
      throw error;
    }
  }

  async createApiCredentials(
    authToken: string,
    payload: { description: string },
  ): Promise<CreateApiCredentialsResult> {
    try {
      const { data } = await this.httpClient.post('/credentials', payload, {
        headers: {
          Authorization: authToken,
        },
      });
      return data;
    } catch (error) {
      ErrorManagement.exception({
        exception: error,
        tags: {
          fileLocation: 'ApiCredentialsService',
          featureFunction: 'createApiCredentials',
        },
        fingerprint: ['api-credentials-service-create'],
      });
      throw error;
    }
  }

  async updateApiCredentials(
    authToken: string,
    payload: UpdateCredentialsRequestPayload,
  ): Promise<{ success: boolean }> {
    try {
      const { data } = await this.httpClient.patch('/credentials', payload, {
        headers: {
          Authorization: authToken,
        },
      });
      return data;
    } catch (error) {
      ErrorManagement.exception({
        exception: error,
        tags: {
          fileLocation: 'ApiCredentialsService',
          featureFunction: 'updateApiCredentials',
        },
        fingerprint: ['api-credentials-service-update'],
      });
      throw error;
    }
  }

  async rotateApiCredentials(
    authToken: string,
    payload: { apiKey: string },
  ): Promise<{ secret: string; }> {
    try {
      const { data } = await this.httpClient.patch('/credentials/reset-password', payload, {
        headers: {
          Authorization: authToken,
        },
      });
      return data;
    } catch (error) {
      ErrorManagement.exception({
        exception: error,
        tags: {
          fileLocation: 'ApiCredentialsService',
          featureFunction: 'rotateApiCredentials',
        },
        fingerprint: ['api-credentials-service-rotate'],
      });
      throw error;
    }
  }
}

export default ApiCredentialsService;
